import React, { useState, useEffect } from 'react';
import { Row, Col, Button, Input, Select, Skeleton, Pagination, message } from 'antd';
import { Search, Star, Heart, Calendar, Wrench } from 'lucide-react';
import { productService, cartService, wishlistService, addressService } from '../services/api';
import BookingModal from '../components/BookingModal';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './Home.css';
import './Services.css';
import './ServiceCatalog.css';

const ServiceCatalog = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [searchQuery, setSearchQuery] = useState('');
  const [coords, setCoords] = useState(null);
  const [bookingProduct, setBookingProduct] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const pageSize = 12;

  const requireAuth = (callback) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      callback();
    } else {
      message.info('Please login or register to continue');
      navigate('/login', { state: { from: window.location.pathname } });
    }
  };

  useEffect(() => {
    const q = searchParams.get('search');
    if (q) setSearchQuery(q);
  }, [searchParams]);

  useEffect(() => {
    // Recommend nearest providers first using the customer's default address.
    const token = localStorage.getItem('authToken');
    if (!token) return;
    addressService
      .getAddresses()
      .then((res) => {
        const list = res?.data?.data || res?.data || [];
        const withCoords = list.filter((a) => a.latitude && a.longitude);
        if (withCoords.length === 0) return;
        const primary = list.find((a) => a.is_default) || withCoords[0];
        setCoords({ lat: primary.latitude, lng: primary.longitude });
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const loadServices = async () => {
      try {
        setLoading(true);
        const params = { limit: 40 };
        if (coords) {
          params.lat = coords.lat;
          params.lng = coords.lng;
          params.sort = 'distance';
        }
        const response = await productService.getServices(params);
        const servicesData = response?.data?.data || response?.data || [];
        if (Array.isArray(servicesData)) {
          setServices(
            servicesData.map((svc) => {
              const realImage = svc.images?.[0]?.url || svc.image || null;
              return {
                ...svc,
                type: 'service',
                badge: svc.badge || 'Service',
                hasImage: Boolean(realImage),
                image:
                  realImage ||
                  `https://placehold.co/300x200?text=${encodeURIComponent(svc.name || 'Service')}`,
                rating: svc.rating || 0,
              };
            }),
          );
          if (coords) setSortBy('distance');
        } else {
          setServices([]);
        }
      } catch (error) {
        console.error('Error loading services:', error);
        setServices([]);
      } finally {
        setLoading(false);
      }
    };
    loadServices();
  }, [coords]);

  const handleAddToCart = (svc) => {
    setBookingProduct(svc);
  };

  const handleServiceBooking = async (bookingData) => {
    if (!bookingProduct) return;
    setBookingLoading(true);
    try {
      await cartService.addToCart(bookingProduct.id, 1, bookingData);
      message.success(`${bookingProduct.name} repair booked! Check your cart when ready.`);
      setBookingProduct(null);
    } catch (error) {
      message.error('Failed to book service');
    } finally {
      setBookingLoading(false);
    }
  };

  const handleSave = async (svc) => {
    try {
      await wishlistService.addToWishlist(svc.id);
      message.success(`${svc.name} saved to wishlist!`);
    } catch (error) {
      message.error('Failed to save to wishlist');
    }
  };

  const formatPrice = (price) => 'Rs ' + Number(price || 0).toLocaleString('en-IN');

  const categories = [
    { value: 'all', label: 'All Fixes' },
    { value: 'repair', label: 'Repair' },
    { value: 'installation', label: 'Installation' },
    { value: 'maintenance', label: 'Maintenance' },
  ];

  const getCategorySlug = (categoryName) => {
    if (!categoryName) return 'other';
    return categoryName.toLowerCase().replace(/\s+/g, '-');
  };

  const filteredServices = services
    .filter(
      (svc) => selectedCategory === 'all' || getCategorySlug(svc.category) === selectedCategory,
    )
    .filter(
      (svc) =>
        !searchQuery ||
        svc.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        svc.description?.toLowerCase().includes(searchQuery.toLowerCase()),
    )
    .sort((a, b) => {
      if (sortBy === 'distance') {
        const ad = a.distance_km != null ? Number(a.distance_km) : Infinity;
        const bd = b.distance_km != null ? Number(b.distance_km) : Infinity;
        return ad - bd;
      }
      if (sortBy === 'price_asc') return (a.price || 0) - (b.price || 0);
      if (sortBy === 'price_desc') return (b.price || 0) - (a.price || 0);
      if (sortBy === 'rating') return (b.rating || 0) - (a.rating || 0);
      return (a.name || '').localeCompare(b.name || '');
    });

  const paginatedServices = filteredServices.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );
  const featuredServices = services.slice(0, 4);

  const renderCard = (svc) => (
    <Col xs={24} sm={12} md={8} lg={6} key={svc.id}>
      <div
        className="cat-card"
        role="button"
        tabIndex={0}
        onClick={() => navigate(`/products/${svc.id}`)}
        onKeyDown={(e) => e.key === 'Enter' && navigate(`/products/${svc.id}`)}
      >
        <div className="cat-cover">
          {svc.hasImage ? (
            <img src={svc.image} alt={svc.name} loading="lazy" />
          ) : (
            <div className="cat-cover-icon">
              <Wrench size={44} strokeWidth={1.6} />
            </div>
          )}
          <span className="cat-badge">{svc.badge}</span>
          {svc.rating > 0 && (
            <span className="cat-rating">
              <Star size={13} fill="#fadb14" strokeWidth={0} />
              {svc.rating}
            </span>
          )}
        </div>
        <div className="cat-body">
          <h3 className="cat-name">{svc.name}</h3>
          {svc.description && (
            <p className="cat-desc">
              {svc.description.substring(0, 90)}
              {svc.description.length > 90 ? '…' : ''}
            </p>
          )}
          <div className="cat-price-row">
            <span className="cat-price-from">From</span>
            <span className="cat-price">{formatPrice(svc.price)}</span>
          </div>
          <div className="cat-actions">
            <Button
              type="primary"
              className="cat-book"
              icon={<Calendar size={16} style={{ verticalAlign: -3 }} />}
              onClick={(e) => {
                e.stopPropagation();
                requireAuth(() => handleAddToCart(svc));
              }}
            >
              Book Repair
            </Button>
            <Button
              className="cat-save"
              aria-label="Save to wishlist"
              onClick={(e) => {
                e.stopPropagation();
                requireAuth(() => handleSave(svc));
              }}
            >
              <Heart size={17} />
            </Button>
          </div>
        </div>
      </div>
    </Col>
  );

  return (
    <div className="home-page">
      {/* Compact page header band */}
      <section className="cc-band cat-band">
        <div className="cc-band-inner">
          <span className="cc-band-eyebrow">Get it fixed</span>
          <h1>Repair &amp; Fixes</h1>
          <p className="cc-band-sub">
            Quick, reliable repairs for your appliances and electronics, booked in minutes.
          </p>
          <div className="cat-search">
            <Input
              size="large"
              allowClear
              placeholder="Search repairs and fixes..."
              prefix={<Search size={17} style={{ color: '#94a3b8' }} />}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>
      </section>

      {/* Featured services */}
      {!loading && featuredServices.length > 0 && (
        <section className="home-section" style={{ paddingBottom: 24 }}>
          <div className="cat-toolbar">
            <div className="cat-toolbar-title">
              <h2>Popular Repairs</h2>
              <p>The fixes our customers book most often</p>
            </div>
          </div>
          <Row gutter={[24, 24]}>{featuredServices.map(renderCard)}</Row>
        </section>
      )}

      {/* All services */}
      <section className="home-section" style={{ paddingTop: 24 }}>
        <div className="cat-toolbar">
          <div className="cat-toolbar-title">
            <h2>All Repairs &amp; Fixes</h2>
            <p>Find the fix you need and book a repair</p>
          </div>
          <div className="cat-filters" style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Select
              value={selectedCategory}
              onChange={(val) => {
                setSelectedCategory(val);
                setCurrentPage(1);
              }}
              options={categories}
            />
            <Select
              value={sortBy}
              onChange={(val) => {
                setSortBy(val);
                setCurrentPage(1);
              }}
              options={[
                { value: 'name', label: 'Name' },
                { value: 'distance', label: 'Nearest to you' },
                { value: 'price_asc', label: 'Price: Low to High' },
                { value: 'price_desc', label: 'Price: High to Low' },
                { value: 'rating', label: 'Rating' },
              ]}
            />
          </div>
        </div>

        {loading ? (
          <Row gutter={[24, 24]}>
            {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
              <Col xs={24} sm={12} md={8} lg={6} key={n}>
                <div className="cat-card" style={{ padding: 18 }}>
                  <Skeleton active />
                </div>
              </Col>
            ))}
          </Row>
        ) : paginatedServices.length === 0 ? (
          <div className="cat-empty">
            <span className="ic">
              <Calendar size={30} strokeWidth={1.6} />
            </span>
            <h4>No repairs found</h4>
            <p>Try adjusting your search or filters</p>
          </div>
        ) : (
          <Row gutter={[24, 24]}>{paginatedServices.map(renderCard)}</Row>
        )}

        {filteredServices.length > pageSize && (
          <div style={{ textAlign: 'center', marginTop: 40 }}>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={filteredServices.length}
              onChange={setCurrentPage}
            />
          </div>
        )}
      </section>

      <BookingModal
        open={!!bookingProduct}
        product={bookingProduct}
        onCancel={() => setBookingProduct(null)}
        onConfirm={handleServiceBooking}
        loading={bookingLoading}
      />
    </div>
  );
};

export default ServiceCatalog;
